/**
 * Typed API error model + classifier.
 *
 * Single source of truth for HTTP error classification on the frontend.
 * All callers should consume `ApiError` instead of doing
 * `error.message.includes('401')` style string matching.
 *
 * Card 1AkpJZp1 — useApiError + toast central
 */

import axios, { AxiosError } from 'axios';

/**
 * Discriminated union over normalized HTTP error categories.
 *
 * - `unauthorized`: 401, missing/expired token. Caller may trigger reauth.
 * - `forbidden`: 403, authenticated but not allowed.
 * - `not_found`: 404.
 * - `conflict`: 409.
 * - `rate_limited`: 429.
 * - `validation`: 422.
 * - `server`: 5xx other than 504.
 * - `timeout`: ECONNABORTED, 504.
 * - `network`: no response (offline, DNS, CORS preflight, connection refused).
 * - `unknown`: anything else (including non-Error throwns).
 */
export type ApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'validation'
  | 'server'
  | 'timeout'
  | 'network'
  | 'unknown';

export interface ApiErrorInit {
  kind: ApiErrorKind;
  status?: number;
  /** Backend-provided code from `response.data.code` or detail. */
  code?: string;
  message: string;
  /** Seconds parsed from `Retry-After` header when status === 429. */
  retryAfterSeconds?: number;
  /** Original axios/JS error. */
  cause?: unknown;
  /** Raw response body when available, for debugging only. */
  responseData?: unknown;
}

/**
 * Normalized error type. Extends Error so all existing code paths that read
 * `.message`, `instanceof Error`, `toString()` keep working unchanged.
 *
 * Adds a tagged `kind`, a numeric `status`, an optional backend `code`,
 * and a structured `cause`.
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status?: number;
  readonly code?: string;
  readonly retryAfterSeconds?: number;
  readonly cause?: unknown;
  readonly responseData?: unknown;
  readonly isApiError = true as const;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.cause = init.cause;
    this.responseData = init.responseData;
    // Preserve prototype chain (TS class extending Error nuance).
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Type guard.
 */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * Extract a numeric Retry-After. Supports the seconds-int form.
 * Returns `undefined` when header is missing or malformed.
 */
export function extractRetryAfterSeconds(error: unknown): number | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const header = error.response?.headers?.['retry-after'];
  if (header == null) return undefined;
  const raw = String(header).trim();
  if (raw === '') return undefined;
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 0) return Math.ceil(n);
  // HTTP-date form: not handling here, return undefined.
  return undefined;
}

/**
 * Try to read a backend-provided error code from common shapes:
 *  - `response.data.code`
 *  - `response.data.detail.code`
 *  - `response.data.error_code`
 */
export function extractBackendCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const data: unknown = error.response?.data;
  if (!data || typeof data !== 'object') return undefined;
  const obj = data as Record<string, unknown>;
  if (typeof obj.code === 'string') return obj.code;
  if (typeof obj.error_code === 'string') return obj.error_code;
  const detail = obj.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const code = (detail as Record<string, unknown>).code;
    if (typeof code === 'string') return code;
  }
  return undefined;
}

/**
 * Try to read a human-readable message from the backend response body.
 * Falls back to `undefined` so the caller can pick a default i18n message.
 */
export function extractBackendMessage(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const data: unknown = error.response?.data;
  if (!data || typeof data !== 'object') return undefined;
  const obj = data as Record<string, unknown>;
  if (typeof obj.message === 'string' && obj.message.trim() !== '') return obj.message;
  const detail = obj.detail;
  if (typeof detail === 'string' && detail.trim() !== '') return detail;
  if (Array.isArray(detail)) {
    // FastAPI validation: keep as a single message.
    const parts = detail
      .filter((d) => d && typeof d === 'object')
      .map((d: Record<string, unknown>) => {
        const loc = Array.isArray(d.loc) ? d.loc.join('.') : '';
        const msg = typeof d.msg === 'string' ? d.msg : '';
        return loc && msg ? `${loc}: ${msg}` : msg || loc;
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join(', ');
  }
  if (detail && typeof detail === 'object') {
    const m = (detail as Record<string, unknown>).message;
    if (typeof m === 'string') return m;
  }
  return undefined;
}

/**
 * Map an HTTP status to a normalized kind.
 */
function statusToKind(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 422) return 'validation';
  if (status === 429) return 'rate_limited';
  if (status === 504) return 'timeout';
  if (status >= 500 && status < 600) return 'server';
  return 'unknown';
}

import {
  DEFAULT_ERROR_MESSAGES,
  STREAM_CONTRACT_ERROR_MESSAGE,
} from '@/i18n/errorMessages';

/**
 * Normalize ANY thrown value into an `ApiError`. Idempotent.
 *
 * - Already-classified `ApiError`s pass through.
 * - `AxiosError` is inspected for `response.status`, `code`, `request`.
 * - Any other `Error` becomes `kind='unknown'`.
 * - Non-Error values become `kind='unknown'`.
 */
export function classifyError(error: unknown): ApiError {
  // Idempotent: already classified.
  if (isApiError(error)) return error;

  if (axios.isAxiosError(error)) {
    const axErr = error as AxiosError;
    const status = axErr.response?.status;

    // Network / no response.
    if (!axErr.response) {
      // ECONNABORTED → axios timeout.
      if (axErr.code === 'ECONNABORTED' || axErr.code === 'ETIMEDOUT') {
        return new ApiError({
          kind: 'timeout',
          message: DEFAULT_ERROR_MESSAGES.timeout,
          cause: axErr,
        });
      }
      return new ApiError({
        kind: 'network',
        message: DEFAULT_ERROR_MESSAGES.network,
        cause: axErr,
      });
    }

    const kind: ApiErrorKind =
      typeof status === 'number' ? statusToKind(status) : 'unknown';
    const backendMsg = extractBackendMessage(axErr);
    const code = extractBackendCode(axErr);
    const retryAfterSeconds =
      kind === 'rate_limited' ? extractRetryAfterSeconds(axErr) : undefined;

    return new ApiError({
      kind,
      status,
      code,
      message: backendMsg ?? DEFAULT_ERROR_MESSAGES[kind],
      retryAfterSeconds,
      cause: axErr,
      responseData: axErr.response.data,
    });
  }

  if (error instanceof Error) {
    return new ApiError({
      kind: 'unknown',
      message: error.message || DEFAULT_ERROR_MESSAGES.unknown,
      cause: error,
    });
  }

  return new ApiError({
    kind: 'unknown',
    message: DEFAULT_ERROR_MESSAGES.unknown,
    cause: error,
  });
}

/**
 * Markers of a stream-wire failure, i.e. an error whose `message` is a
 * validator dump rather than prose a person can act on.
 *
 * `Type validation failed` is what `@ai-sdk/provider-utils` prefixes; the Zod
 * issue codes cover the case where only the inner report survives (a wrapped
 * or re-thrown error).
 */
const WIRE_FAILURE_MARKERS = [
  'Type validation failed',
  'invalid_union',
  'unrecognized_keys',
  'Invalid JSON response',
] as const;

/**
 * Classify a chat-stream error WITHOUT letting a validator dump reach the user.
 *
 * `classifyError` surfaces `error.message` verbatim for any plain `Error`,
 * which is right for a backend message and wrong for a wire failure: the AI
 * SDK's `TypeValidationError` carries the whole rejected chunk plus every Zod
 * issue in `message`, and `useApiError` renders that into a toast. Card
 * 2OaQ3yFr shipped exactly that to production — a raw JSON + Zod dump in the
 * chat.
 *
 * The raw text is NOT discarded: it stays in `cause`, so the structured log
 * and Sentry still get it. Only the user-facing string is replaced.
 */
export function sanitizeStreamError(error: unknown): ApiError {
  if (isApiError(error)) return error;

  const raw = error instanceof Error ? error.message : '';
  const isWireFailure = WIRE_FAILURE_MARKERS.some((marker) =>
    raw.includes(marker),
  );
  if (!isWireFailure) return classifyError(error);

  return new ApiError({
    kind: 'server',
    message: STREAM_CONTRACT_ERROR_MESSAGE,
    cause: error,
  });
}
